import { useState, useEffect, useCallback, useRef, Fragment } from 'react';
import { toast } from 'sonner';
import { sourcingAPI, getErrorDetail } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../ui/alert-dialog';
import { Loader2, Pencil, Check, X, RefreshCw, Search, ChevronLeft, ChevronRight, EyeOff, Zap } from 'lucide-react';
import { TIER_BADGE, badgeClass, BROKERBIN_CONDITIONS } from './sourcingUtils';

const money = (v) => (v === null || v === undefined ? '—' : `$${Number(v).toFixed(2)}`);

function humanizeScanStatus(status) {
  if (status === 'DONE') return 'complete';
  if (status === 'STOPPED_QUOTA') return 'stopped (budget)';
  if (status === 'RUNNING') return 'running';
  return status;
}

export default function NeedsTab() {
  const { canAccess } = useAuth();
  const canEdit = canAccess('SOURCING', 'EDIT');
  const [loading, setLoading] = useState(true);
  const [parts, setParts] = useState([]);
  const [view, setView] = useState('default');
  const [scope, setScope] = useState('mine');
  const [editingSku, setEditingSku] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Filters (item 9)
  const [searchable, setSearchable] = useState('any');
  const [tier, setTier] = useState('any');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;

  // Refresh / Scan now (items 8 & 10)
  const [refreshing, setRefreshing] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const pollRef = useRef(null);

  // Bulk "Mark not searchable" (item 8, 25 Feb 2026 quota round)
  const [selected, setSelected] = useState(new Set());
  const [bulkReasonOpen, setBulkReasonOpen] = useState(false);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  // Admin Force Rescan (3 Nov 2026)
  const [forceMode, setForceMode] = useState(null); // 'selected' | 'all' | null
  const [forcePreview, setForcePreview] = useState(null);
  const [forceLoadingPreview, setForceLoadingPreview] = useState(false);
  const [forceStarting, setForceStarting] = useState(false);

  const load = useCallback(async (v = view, p = page) => {
    setLoading(true);
    try {
      const params = { view: v, page: p };
      if (searchable !== 'any') params.searchable = searchable === 'yes';
      if (tier !== 'any') params.tier = tier;
      if (q) params.q = q;
      const res = await sourcingAPI.listParts(params);
      setParts(res.data.parts);
      setScope(res.data.scope);
      setTotal(res.data.total);
    } catch (e) {
      toast.error('Failed to load Needs');
    } finally {
      setLoading(false);
    }
  }, [view, page, searchable, tier, q]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  useEffect(() => { setSelected(new Set()); }, [parts]);

  const toggleSelect = (skuId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId); else next.add(skuId);
      return next;
    });
  };

  const selectableSkus = parts.filter((p) => p.searchable).map((p) => p.sku_id);
  const allSelected = selectableSkus.length > 0 && selectableSkus.every((id) => selected.has(id));
  const toggleSelectAll = () => {
    setSelected(allSelected ? new Set() : new Set(selectableSkus));
  };

  const submitBulkMarkNotSearchable = async () => {
    if (!bulkReason.trim()) { toast.error('A reason is required'); return; }
    setBulkSaving(true);
    try {
      const res = await sourcingAPI.bulkMarkNotSearchable(Array.from(selected), bulkReason.trim());
      toast.success(`Marked ${res.data.updated} SKU${res.data.updated === 1 ? '' : 's'} not searchable`);
      setBulkReasonOpen(false);
      setBulkReason('');
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(getErrorDetail(e, 'Failed to mark SKUs not searchable'));
    } finally {
      setBulkSaving(false);
    }
  };

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQ(qInput.trim());
  };

  const refreshFromTargets = async () => {
    setRefreshing(true);
    try {
      const res = await sourcingAPI.refreshParts();
      const { created, updated, no_target } = res.data;
      toast.success(`Refreshed from Targets — ${created} created, ${updated} updated, ${no_target} dropped`);
      setPage(1);
      load(view, 1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const scanNow = async () => {
    setScanning(true);
    setScanResult(null);
    try {
      const res = await sourcingAPI.scanNow();
      pollScan(res.data.scan_id, setScanning);
    } catch (e) {
      setScanning(false);
      toast.error(getErrorDetail(e, 'Scan failed to start'));
    }
  };

  // Admin Force Rescan (3 Nov 2026): shared poller — same underlying scan entity as
  // the regular Scan Now, just started via a different endpoint.
  const pollScan = (scanId, setRunning) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let attempts = 0;
    pollRef.current = setInterval(async () => {
      attempts += 1;
      try {
        const scansRes = await sourcingAPI.listScans();
        const scan = (scansRes.data.scans || []).find((s) => s.id === scanId);
        if (scan && scan.status !== 'RUNNING') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunning(false);
          setScanResult(scan);
          load();
        } else if (attempts >= 60) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setRunning(false);
          toast.error('Scan is taking longer than expected — check back on the Needs tab shortly');
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setRunning(false);
        toast.error('Lost connection while checking scan status');
      }
    }, 2000);
  };

  const openForceDialog = async (mode) => {
    if (mode === 'selected' && selected.size === 0) { toast.error('Select at least one SKU first'); return; }
    setForceMode(mode);
    setForcePreview(null);
    setForceLoadingPreview(true);
    try {
      const partIds = mode === 'selected' ? Array.from(selected) : [];
      const res = await sourcingAPI.forceRescanPreview(mode, partIds);
      setForcePreview(res.data);
    } catch (e) {
      toast.error(getErrorDetail(e, 'Failed to load force rescan estimate'));
      setForceMode(null);
    } finally {
      setForceLoadingPreview(false);
    }
  };

  const confirmForceRescan = async () => {
    setForceStarting(true);
    try {
      const partIds = forceMode === 'selected' ? Array.from(selected) : [];
      const res = await sourcingAPI.forceRescan(forceMode, partIds);
      toast.success(`Force rescan started — ${res.data.parts_to_scan} part(s)`);
      setForceMode(null);
      setScanResult(null);
      setScanning(true);
      pollScan(res.data.scan_id, setScanning);
    } catch (e) {
      toast.error(getErrorDetail(e, 'Failed to start force rescan'));
    } finally {
      setForceStarting(false);
    }
  };

  const openEdit = (part) => {
    setEditingSku(part.sku_id);
    setForm({
      search_part: part.search_part || '', searchable: part.searchable,
      manufacturer: part.manufacturer || '', qty_needed: part.qty_needed || 0,
      accepted_conditions: part.accepted_conditions || [],
    });
  };

  const toggleCondition = (code) => {
    setForm((f) => ({
      ...f,
      accepted_conditions: f.accepted_conditions.includes(code)
        ? f.accepted_conditions.filter((c) => c !== code)
        : [...f.accepted_conditions, code],
    }));
  };

  const save = async (part) => {
    setSaving(true);
    try {
      const payload = {};
      if (form.search_part !== (part.search_part || '')) payload.search_part = form.search_part || null;
      if (form.searchable !== part.searchable) payload.searchable = form.searchable;
      if (form.manufacturer !== (part.manufacturer || '')) payload.manufacturer = form.manufacturer;
      if (Number(form.qty_needed) !== (part.qty_needed || 0)) payload.qty_needed = Number(form.qty_needed) || 0;
      const beforeConditions = (part.accepted_conditions || []).join(',');
      if (form.accepted_conditions.join(',') !== beforeConditions) payload.accepted_conditions = form.accepted_conditions;
      if (Object.keys(payload).length === 0) { setEditingSku(null); setSaving(false); return; }
      await sourcingAPI.updatePart(part.sku_id, payload);
      toast.success('Saved');
      setEditingSku(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading && parts.length === 0) return <div className="flex justify-center py-16" data-testid="needs-loading"><Loader2 className="animate-spin" /></div>;

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  let firstNonSearchableSeen = false;

  return (
    <div className="space-y-4" data-testid="sourcing-needs-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {scope !== 'all' ? (
            <>
              <p className="text-sm text-muted-foreground">Showing your assigned SKUs only.</p>
              <Button variant="outline" size="sm" onClick={() => { setView('all'); setPage(1); }} data-testid="needs-show-all-btn">Show All SKUs</Button>
            </>
          ) : view === 'all' && (
            <Button variant="outline" size="sm" onClick={() => { setView('default'); setPage(1); }} data-testid="needs-show-mine-btn">Show My SKUs</Button>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            {selected.size > 0 && (
              <Button variant="outline" size="sm" className="text-destructive" onClick={() => setBulkReasonOpen(true)} data-testid="needs-bulk-mark-not-searchable-btn">
                <EyeOff className="h-3.5 w-3.5 mr-1" /> Mark not searchable ({selected.size})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={refreshFromTargets} disabled={refreshing} data-testid="needs-refresh-from-targets-btn">
              {refreshing ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
              Refresh from Targets
            </Button>
            <Button size="sm" onClick={scanNow} disabled={scanning} data-testid="needs-scan-now-btn">
              {scanning ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Search className="h-3.5 w-3.5 mr-1" />}
              {scanning ? 'Scanning…' : 'Scan Now'}
            </Button>
            {selected.size > 0 && (
              <Button variant="outline" size="sm" onClick={() => openForceDialog('selected')} disabled={scanning} data-testid="needs-force-rescan-selected-btn">
                <Zap className="h-3.5 w-3.5 mr-1" /> Force rescan selected ({selected.size})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => openForceDialog('all')} disabled={scanning} data-testid="needs-force-rescan-all-btn">
              <Zap className="h-3.5 w-3.5 mr-1" /> Force rescan all today
            </Button>
          </div>
        )}
      </div>

      {scanResult && (
        <Card className="rounded-sm border-primary/40" data-testid="needs-scan-result">
          <CardContent className="p-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="font-medium">
              {scanResult.kind === 'FORCE' ? 'Force rescan' : 'Scan'} {humanizeScanStatus(scanResult.status)}:
            </span>
            <span>Needs scanned: <b>{scanResult.parts_scanned}</b>/{scanResult.parts_total}</span>
            <span>Listings stored: <b>{scanResult.listings_stored ?? 0}</b></span>
            <span>Drafts created: <b>{scanResult.drafts_created ?? 0}</b></span>
            <span>Calls used today: <b>{scanResult.calls_used ?? '—'}</b></span>
            {scanResult.budget_stopped_message && (
              <span className="text-amber-700" data-testid="needs-scan-result-budget-stopped">{scanResult.budget_stopped_message}</span>
            )}
            {scanResult.errors?.length > 0 && (
              <span className="text-destructive" data-testid="needs-scan-result-errors">
                Errors: {scanResult.errors.slice(0, 3).join('; ')}{scanResult.errors.length > 3 ? '…' : ''}
              </span>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="rounded-sm">
        <CardContent className="p-3 flex flex-wrap items-center gap-2">
          <form onSubmit={applySearch} className="flex items-center gap-1">
            <Input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Search SKU or part #"
                   className="h-8 w-48" data-testid="needs-search-input" />
            <Button type="submit" size="sm" variant="outline" className="h-8" data-testid="needs-search-submit-btn">Search</Button>
          </form>
          <Select value={searchable} onValueChange={(v) => { setSearchable(v); setPage(1); }}>
            <SelectTrigger className="h-8 w-36" data-testid="needs-filter-searchable"><SelectValue placeholder="Searchable" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All (searchable)</SelectItem>
              <SelectItem value="yes">Searchable only</SelectItem>
              <SelectItem value="no">Not searchable</SelectItem>
            </SelectContent>
          </Select>
          <Select value={tier} onValueChange={(v) => { setTier(v); setPage(1); }}>
            <SelectTrigger className="h-8 w-32" data-testid="needs-filter-tier"><SelectValue placeholder="Tier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All tiers</SelectItem>
              <SelectItem value="HOT">HOT</SelectItem>
              <SelectItem value="WATCH">WATCH</SelectItem>
              <SelectItem value="NORMAL">NORMAL</SelectItem>
            </SelectContent>
          </Select>
          {(q || searchable !== 'any' || tier !== 'any') && (
            <Button variant="ghost" size="sm" className="h-8" data-testid="needs-filter-clear-btn"
                    onClick={() => { setQ(''); setQInput(''); setSearchable('any'); setTier('any'); setPage(1); }}>
              Clear filters
            </Button>
          )}
          <span className="ml-auto text-xs text-muted-foreground" data-testid="needs-total-count">{total} need{total === 1 ? '' : 's'}</span>
        </CardContent>
      </Card>

      <Card className="rounded-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm" data-testid="needs-table">
            <thead>
              <tr className="text-xs font-medium text-muted-foreground border-b border-border">
                {canEdit && (
                  <th className="text-center px-2 py-2">
                    {selectableSkus.length > 0 && (
                      <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} data-testid="needs-select-all-checkbox" />
                    )}
                  </th>
                )}
                <th className="text-left px-4 py-2">SKU / Item</th>
                <th className="text-left px-2 py-2">PM</th>
                <th className="text-right px-2 py-2">Target $</th>
                <th className="text-right px-2 py-2">Qty</th>
                <th className="text-left px-2 py-2">Search Part</th>
                <th className="text-center px-2 py-2">Searchable</th>
                <th className="text-left px-2 py-2">Conditions</th>
                <th className="text-right px-2 py-2">Matches Today</th>
                <th className="text-left px-2 py-2">Last Scanned</th>
                <th className="text-left px-2 py-2">Tier</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {parts.map((p) => {
                const isEditing = editingSku === p.sku_id;
                const showGroupDivider = !p.searchable && !firstNonSearchableSeen && (firstNonSearchableSeen = true);
                const colSpan = canEdit ? 12 : 11;
                return (
                  <Fragment key={p.sku_id}>
                    {showGroupDivider && (
                      <tr key={`divider-${p.sku_id}`}>
                        <td colSpan={colSpan} className="px-4 py-1 bg-muted/40 text-xs font-medium text-muted-foreground" data-testid="needs-not-searchable-divider">
                          Not searchable
                        </td>
                      </tr>
                    )}
                    <tr key={p.sku_id} data-testid={`needs-row-${p.sku_id}`} className={!p.searchable ? 'bg-muted/10' : ''}>
                      {canEdit && (
                        <td className="px-2 py-2 align-top text-center">
                          {p.searchable && (
                            <Checkbox checked={selected.has(p.sku_id)} onCheckedChange={() => toggleSelect(p.sku_id)} data-testid={`needs-row-checkbox-${p.sku_id}`} />
                          )}
                        </td>
                      )}
                      <td className="px-4 py-2 align-top">
                        <div className="font-mono text-xs">{p.sku_id}</div>
                        <div className="text-muted-foreground truncate max-w-[220px]">{p.item_name || '—'}</div>
                      </td>
                      <td className="px-2 py-2 align-top">{p.pm_name || '—'}</td>
                      <td className="px-2 py-2 align-top text-right">{money(p.target_price_usd)}</td>
                      <td className="px-2 py-2 align-top text-right">{p.qty_needed ?? '—'}</td>
                      <td className="px-2 py-2 align-top">{p.search_part || <span className="italic text-muted-foreground">none</span>}</td>
                      <td className="px-2 py-2 align-top text-center">
                        {p.searchable ? <Badge variant="outline" className="bg-emerald-600/15 text-emerald-600 border-emerald-600/30">Yes</Badge> : (
                          <div className="flex flex-col items-center gap-0.5">
                            <Badge variant="outline">No</Badge>
                            <span className="text-[10px] text-muted-foreground" data-testid={`needs-reason-${p.sku_id}`}>{p.not_searchable_reason}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top">{(p.accepted_conditions || []).join(', ') || '—'}</td>
                      <td className="px-2 py-2 align-top text-right">
                        {p.matches_today ?? 0}
                        {p.low_qty_matches_today > 0 && (
                          <div className="text-[10px] text-amber-700" data-testid={`needs-low-qty-${p.sku_id}`}>
                            {p.low_qty_matches_today} low qty
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-2 align-top whitespace-nowrap">{p.last_scanned_at ? new Date(p.last_scanned_at).toLocaleDateString() : '—'}</td>
                      <td className="px-2 py-2 align-top">
                        <Badge variant="outline" className={badgeClass(TIER_BADGE, p.tier)} data-testid={`needs-tier-${p.sku_id}`}>{p.tier || 'NORMAL'}</Badge>
                      </td>
                      <td className="px-2 py-2 align-top text-right">
                        {(canEdit || scope === 'mine') && !isEditing && (
                          <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`needs-edit-btn-${p.sku_id}`}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                    {isEditing && (
                      <tr key={`edit-${p.sku_id}`}>
                        <td colSpan={colSpan} className="px-4 pb-4 pt-1 bg-muted/30" data-testid={`needs-inline-edit-${p.sku_id}`}>
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-medium text-muted-foreground">Search Part</label>
                                <Input value={form.search_part} onChange={(e) => setForm({ ...form, search_part: e.target.value })} data-testid="needs-form-search-part" />
                              </div>
                              <div>
                                <label className="text-xs font-medium text-muted-foreground">Manufacturer</label>
                                <Input value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} data-testid="needs-form-manufacturer" />
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-medium text-muted-foreground block mb-1">Accepted Conditions (BrokerBin codes)</label>
                              <div className="flex flex-wrap gap-2" data-testid="needs-form-conditions">
                                {BROKERBIN_CONDITIONS.map((code) => (
                                  <button
                                    key={code}
                                    type="button"
                                    onClick={() => toggleCondition(code)}
                                    data-testid={`needs-condition-${code.replace('/', '-')}`}
                                    className={`px-2 py-1 rounded-sm text-xs border transition-colors ${form.accepted_conditions.includes(code) ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-border'}`}
                                  >
                                    {code}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div>
                                <label className="text-xs font-medium text-muted-foreground">Qty Needed</label>
                                <Input type="number" className="w-28" value={form.qty_needed} onChange={(e) => setForm({ ...form, qty_needed: e.target.value })} data-testid="needs-form-qty" />
                              </div>
                              <div className="flex items-center gap-2">
                                <label className="text-sm">Searchable</label>
                                <Switch checked={form.searchable} onCheckedChange={(v) => setForm({ ...form, searchable: v })} data-testid="needs-form-searchable" />
                              </div>
                            </div>
                            <div className="flex items-center gap-2 pt-1">
                              <Button size="sm" onClick={() => save(p)} disabled={saving} data-testid="needs-save-btn">
                                <Check className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingSku(null)} data-testid="needs-cancel-btn">
                                <X className="h-3.5 w-3.5 mr-1" /> Cancel
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {parts.length === 0 && (
                <tr><td colSpan={canEdit ? 12 : 11} className="p-8 text-center text-sm text-muted-foreground">No sourcing needs found.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <AlertDialog open={bulkReasonOpen} onOpenChange={setBulkReasonOpen}>
        <AlertDialogContent data-testid="needs-bulk-mark-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {selected.size} SKU{selected.size === 1 ? '' : 's'} not searchable?</AlertDialogTitle>
            <AlertDialogDescription>
              These SKUs will stop being included in the daily/manual scan. A reason is required and will show on the Needs tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input value={bulkReason} onChange={(e) => setBulkReason(e.target.value)} placeholder="e.g. Test SKU, not a real part"
                 data-testid="needs-bulk-mark-reason-input" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="needs-bulk-mark-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitBulkMarkNotSearchable} disabled={bulkSaving || !bulkReason.trim()} data-testid="needs-bulk-mark-confirm-btn">
              {bulkSaving ? 'Saving...' : 'Mark not searchable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!forceMode} onOpenChange={(o) => !o && setForceMode(null)}>
        <AlertDialogContent data-testid="needs-force-rescan-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {forceMode === 'selected' ? `Force rescan ${selected.size} SKU${selected.size === 1 ? '' : 's'}?` : 'Force rescan every searchable need?'}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="needs-force-rescan-estimate">
              {forceLoadingPreview || !forcePreview ? 'Loading estimate...' : (
                `This uses about ${forcePreview.estimated_calls} BrokerBin calls; ${forcePreview.calls_left_today} left today; `
                + `${forcePreview.reserve_calls} kept back for RFQ sends and live searches `
                + `(${forcePreview.usable_calls} usable for this rescan). `
                + `${forcePreview.runs_remaining} force rescan${forcePreview.runs_remaining === 1 ? '' : 's'} left today.`
              )}
              {!forceLoadingPreview && forcePreview && forcePreview.usable_calls <= 0 && (
                <div className="text-destructive mt-1" data-testid="needs-force-rescan-no-budget">
                  No calls left for a force rescan right now — today's budget is fully reserved for RFQ sends and live searches.
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="needs-force-rescan-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmForceRescan}
                                disabled={forceStarting || forceLoadingPreview || !forcePreview || forcePreview.runs_remaining === 0 || forcePreview.usable_calls <= 0}
                                data-testid="needs-force-rescan-confirm-btn">
              {forceStarting ? 'Starting...' : 'Force rescan'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {total > pageSize && (
        <div className="flex items-center justify-between" data-testid="needs-pagination">
          <span className="text-xs text-muted-foreground">Page {page} of {totalPages} ({total} total)</span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="needs-page-prev-btn">
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="needs-page-next-btn">
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
